import {
  subscribeToCustomer,
  subscribeToJob,
  subscribeToJobs,
  subscribeToPains,
  subscribeToParticipants,
  subscribeToPicks,
  subscribeToSession,
} from "@/services/realtime"
import type { Unsubscribe } from "@/services/realtime"
import { joinSession, leaveSession } from "@/services/participants"
import type { JoinSessionInput } from "@/services/participants"
import {
  clearStoredParticipantId,
  getStoredParticipantId,
  newGuestId,
  setStoredParticipantId,
} from "@/utils/sessionIdentity"
import { useSessionStore } from "./sessionStore"

/**
 * Wires Firestore listeners into the global store:
 *
 *   Firestore -> realtime listeners -> store -> React UI
 *
 * Nothing else writes session state; the store is a projection of Firestore.
 * Who this browser IS comes from two other places — localStorage for a guest,
 * the auth store for a facilitator — and neither is a claim about the session,
 * only a key for reading one.
 */

interface Held {
  key: string
  teardown: () => void
  /**
   * How many callers currently hold this connection. Reference counted because
   * React StrictMode double-invokes effects in development, and more than one
   * component may legitimately mount against the same key — without this, the
   * first cleanup would kill listeners the others still rely on, or we would
   * open duplicate listeners for the same data.
   */
  refs: number
}

/**
 * A single-slot, reference-counted subscription: at most one key is live at a
 * time, shared by every holder. Used for both the session and the customer so
 * the two behave identically — an asymmetry here is exactly the kind of thing
 * that silently leaks listeners.
 */
function createSharedConnection(
  open: (key: string) => Unsubscribe[],
  beforeOpen?: (key: string) => void
) {
  let held: Held | null = null

  function connect(key: string): Unsubscribe {
    // Already listening to this key: share the existing subscription.
    if (held && held.key === key) {
      const shared = held
      shared.refs += 1
      return () => release(shared)
    }

    // Switching keys: drop the previous listeners first, so nothing belonging
    // to the old key can land in the store afterwards.
    if (held) {
      held.teardown()
      held = null
    }

    beforeOpen?.(key)

    const unsubscribes = open(key)
    const current: Held = {
      key,
      refs: 1,
      teardown: () => {
        for (const unsubscribe of unsubscribes) unsubscribe()
      },
    }
    held = current

    return () => release(current)
  }

  /**
   * Compared by object identity, not by key: reconnecting to the same key
   * creates a NEW holder, and a late cleanup belonging to the previous one must
   * not decrement — let alone tear down — its replacement.
   */
  function release(holder: Held): void {
    if (held !== holder) return // superseded generation; nothing to do
    holder.refs -= 1
    if (holder.refs > 0) return
    holder.teardown()
    held = null
  }

  function disconnect(): void {
    if (!held) return
    held.teardown()
    held = null
  }

  return { connect, disconnect }
}

const sessionConnection = createSharedConnection(
  (sessionId) => {
    const fail = (error: Error) => useSessionStore.getState().setConnectionError(error.message)

    /*
     * Firestore keeps delivering a subcollection's documents after its parent
     * document is deleted, because deleting a document does not delete what is
     * nested under it. Both flags below exist to stop those orphans reaching the
     * store: null means "not answered yet" (so nothing is suppressed during the
     * normal race between listeners on first load), false means "confirmed gone".
     */
    let sessionExists: boolean | null = null
    let jobExists: boolean | null = null

    /*
     * The current job is a DEPENDENT subscription: which job is active is itself
     * part of the session document, so it can only be opened once the session
     * snapshot arrives, and has to be re-pointed when the facilitator opens
     * another. Held in a closure the connection's teardown also closes, so
     * switching jobs cannot leak listeners and a torn-down session cannot leave
     * a job listener running.
     */
    let jobId: string | null = null
    let jobUnsubscribes: Unsubscribe[] = []

    const closeJob = () => {
      for (const unsubscribe of jobUnsubscribes) unsubscribe()
      jobUnsubscribes = []
    }

    const pointAtJob = (nextJobId: string | null) => {
      if (nextJobId === jobId) return
      closeJob()
      jobId = nextJobId
      jobExists = null

      const store = useSessionStore.getState()
      // Drop the previous job's data immediately, so no screen can render one
      // job's picks against another job.
      store.applyJobSnapshot(null)
      if (!nextJobId) return

      jobUnsubscribes = [
        subscribeToJob(
          sessionId,
          nextJobId,
          (job) => {
            jobExists = job !== null
            useSessionStore.getState().applyJobSnapshot(job)
          },
          fail
        ),
        subscribeToPicks(
          sessionId,
          nextJobId,
          (picks) => {
            // The job this points at is gone: its picks are orphans, and
            // rendering them would show one job's hand with no job behind it.
            if (jobExists === false) return
            useSessionStore.getState().setPicks(picks)
          },
          fail
        ),
      ]
    }

    return [
      subscribeToSession(
        sessionId,
        (session) => {
          sessionExists = session !== null
          useSessionStore.getState().applySessionSnapshot(session)
          pointAtJob(session?.currentJobId ?? null)
        },
        fail
      ),
      subscribeToParticipants(
        sessionId,
        (participants) => {
          if (sessionExists === false) return
          useSessionStore.getState().setParticipants(participants)
        },
        fail
      ),
      subscribeToPains(
        sessionId,
        (pains) => {
          if (sessionExists === false) return
          useSessionStore.getState().setPains(pains)
        },
        fail
      ),
      subscribeToJobs(
        sessionId,
        (jobs) => {
          if (sessionExists === false) return
          useSessionStore.getState().setJobs(jobs)
        },
        fail
      ),
      closeJob,
    ]
  },
  (sessionId) => {
    const store = useSessionStore.getState()
    // Drop anything belonging to a previously viewed session.
    store.clearSessionData()

    // Restore who this browser is, so a reload keeps its seat: a guest does not
    // create a duplicate document and keeps everything already attributed to
    // them. A facilitator needs nothing restored — they are whoever is signed
    // in, and the session document says whether that is them.
    const storedParticipantId = getStoredParticipantId(sessionId)
    if (storedParticipantId) store.setCurrentParticipantId(storedParticipantId)

    store.setConnection("connecting")
  }
)

/**
 * Subscribes to a session and everything under it. Reference counted: safe to
 * call from several components and under StrictMode. Call the returned function
 * to drop this hold.
 */
export const connectSession = sessionConnection.connect

const customerConnection = createSharedConnection(
  (customerId) => [
    subscribeToCustomer(
      customerId,
      (customer) => useSessionStore.getState().applyCustomerSnapshot(customer),
      // Its own status: a dead customer listener must not send a live session
      // screen to the terminal "stopped listening" state. A guest is REFUSED
      // this document by the rules, which arrives here as an error — the session
      // screen reads the customer's name off the session instead.
      (error) => useSessionStore.getState().setCustomerError(error.message)
    ),
  ],
  () => {
    const store = useSessionStore.getState()
    // Drop the previous customer before the new one answers, so no screen shows
    // one customer's name over another's sessions.
    store.applyCustomerSnapshot(null)
    store.setCustomerConnection("connecting")
  }
)

/**
 * Keeps the current customer live. Independent of the session subscription, but
 * reference counted the same way.
 */
export const connectCustomer = customerConnection.connect

/**
 * Joins the session as a guest and remembers the identity for this browser. The
 * participant document itself arrives through the realtime listener, so this
 * only records *who we are*, never the roster.
 *
 * The id is minted here, before the write, and reused if this browser has been
 * in this session before — so a guest coming back through the link lands on the
 * seat that already holds their pain points instead of a second, empty one.
 */
export async function joinSessionAsMe(
  sessionId: string,
  input: Omit<JoinSessionInput, "participantId">
): Promise<string> {
  const participantId = getStoredParticipantId(sessionId) ?? newGuestId()
  await joinSession(sessionId, { ...input, participantId })
  setStoredParticipantId(sessionId, participantId)

  useSessionStore.getState().setCurrentParticipantId(participantId)
  return participantId
}

/** Removes this browser's participant from the session and forgets the seat. */
export async function leaveSessionAsMe(sessionId: string): Promise<void> {
  const { currentParticipantId, setCurrentParticipantId } = useSessionStore.getState()
  if (currentParticipantId) await leaveSession(sessionId, currentParticipantId)
  clearStoredParticipantId(sessionId)
  setCurrentParticipantId(null)
}

/** Tears every subscription down and returns the store to its initial state. */
export function disconnectSession(): void {
  sessionConnection.disconnect()
  customerConnection.disconnect()
  useSessionStore.getState().reset()
}
